import { useState, ReactNode } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface TroubleshootingStepProps {
  stepNumber: number;
  title: string;
  description: string | ReactNode;
  instructions: string;
  actionText?: string;
  actionUrl?: string;
  completedText?: string;
  isActive: boolean;
  onComplete: () => void;
  onSkip?: () => void;
  isCompleted: boolean;
}

const TroubleshootingStep = ({
  stepNumber,
  title,
  description,
  instructions,
  actionText,
  actionUrl,
  completedText = "I've completed this step",
  isActive,
  onComplete,
  onSkip,
  isCompleted,
}: TroubleshootingStepProps) => {
  const [completed, setCompleted] = useState(isCompleted);
  const handleComplete = () => {
    setCompleted(true);
    onComplete();
  };

  return (
    <Card
      className={cn(
        "transition-all duration-300 overflow-hidden max-sm:p-3",
        isActive ? "opacity-100 border-primary" : "opacity-70 border-muted",
        completed && "border-success bg-success/5"
      )}
    >
      <CardHeader>
        <div className="flex items-center relative">
          <div
            className={cn(
              "flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full mr-3",
              completed ? "bg-success text-white" : "bg-primary/10 text-primary"
            )}
          >
            {completed ? (
              <Check className="w-5 h-5" />
            ) : (
              <span className="font-bold">{stepNumber}</span>
            )}
          </div>
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
        <div className="ml-11 max-sm:ml-0 max-sm:mt-2">{description}</div>
      </CardHeader>

      {isActive && !completed && (
        <CardContent className="space-y-4">
          <div className="text-sm">{instructions}</div>

          {actionText && actionUrl && (
            <Button
              variant="outline"
              className="bg-white hover:bg-muted border-primary text-primary"
              asChild
            >
              <a href={actionUrl} target="_blank" rel="noopener noreferrer">
                {actionText}
              </a>
            </Button>
          )}
        </CardContent>
      )}

      {isActive && (
        <CardFooter className="flex justify-between pt-2 gap-2 max-sm:flex-col max-sm:items-stretch">
          <Button
            variant={completed ? "ghost" : "default"}
            className={cn(
              completed
                ? "hover:bg-transparent text-success"
                : "bg-primary hover:bg-primary/90"
            )}
            onClick={handleComplete}
            disabled={completed}
          >
            {completed ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Completed
              </>
            ) : (
              completedText
            )}
          </Button>

          {onSkip && !completed && (
            <Button variant="ghost" onClick={onSkip}>
              Skip
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
};

export default TroubleshootingStep;
